<script setup lang="ts">
	import { CheckCircle2, LockKeyhole } from 'lucide-vue-next'
	import { useRoute } from 'vue-router'

	import ContextHelp from '@/components/ContextHelp.vue'

	import type {
		HotelReportAccessMap,
		HotelReportType,
	} from '../composables/useHotelReportAccess'

	defineProps<{ access?: HotelReportAccessMap | null }>()

	const route = useRoute()
	const sources = [
		{
			name: 'reports.hotels',
			type: 'local' as HotelReportType,
			title: 'Booking nuevo',
			description: 'Sólo reservas del nuevo sistema.',
		},
		{
			name: 'reports.hotels.legacy',
			type: 'legacy' as HotelReportType,
			title: 'Booking actual · Reporte 2.0',
			description: 'Reservas de ambos sistemas.',
		},
	]
</script>

<template>
	<nav
		aria-label="Fuente del reporte de hotelería"
		class="grid gap-2 text-base md:grid-cols-3"
	>
		<template
			v-for="source in sources"
			:key="source.name"
		>
			<RouterLink
				v-if="access?.[source.type]?.allowed"
				:to="{ name: source.name }"
				class="rounded-xl border px-4 py-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-ef-500"
				:class="
					route.name === source.name ?
						'border-blue-ef-500 bg-blue-50/60 shadow-sm'
					:	'border-slate-200 bg-white hover:border-blue-ef-300'
				"
			>
				<span
					class="flex items-center justify-between gap-3 font-semibold text-blue-ef-400"
				>
					{{ source.title }}
					<CheckCircle2
						v-if="route.name === source.name"
						class="h-5 w-5 shrink-0 text-blue-ef-500"
						aria-hidden="true"
					/>
				</span>
				<span class="mt-1 block text-sm leading-snug text-slate-600">{{
					source.description
				}}</span>
			</RouterLink>
			<div
				v-else
				class="rounded-xl border border-slate-300 bg-slate-100 px-4 py-3"
			>
				<div class="flex items-center justify-between gap-2">
					<span class="font-semibold text-slate-700">{{ source.title }}</span>
					<ContextHelp :title="`Acceso: ${source.title}`">{{
						access?.[source.type]?.message ||
						'Selecciona una empresa y espera a que se verifique el acceso.'
					}}</ContextHelp>
				</div>
				<span class="flex items-center gap-2 text-sm text-slate-600"
					><LockKeyhole
						class="h-4 w-4"
						aria-hidden="true"
					/>{{ source.description }}</span
				>
			</div>
		</template>
		<div
			role="group"
			aria-label="Formato operativo, próximamente"
			aria-disabled="true"
			class="cursor-not-allowed rounded-xl border border-slate-300 bg-slate-200 px-4 py-3"
		>
			<div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
				<span class="font-semibold text-slate-700">Formato operativo</span>
				<span
					class="rounded-full bg-white px-2 py-0.5 text-sm font-medium text-slate-600"
					>Próximamente</span
				>
			</div>
			<span class="mt-1 block text-sm leading-snug text-slate-600"
				>Vista operativa del Booking actual.</span
			>
		</div>
	</nav>
</template>
